import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  Visibility,
  Share as ShareIcon,
  Laptop,
  Person,
  ArrowForward,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Star,
  School,
  Work
} from '@mui/icons-material';
import { getAvatarUrl } from '../../../../utils/imageUtils';
import ShareProfileDialog from '../../../../Components/Dialog/ShareProfile';
import ImageWithSkeleton from '../../../../Components/ImageWithSkeleton';
import circleContainer from '../../../../assets/images/circle-container.svg';
import config from 'config';

const ModernTutorCard = ({ tutor }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const locationUrl = useLocation();
  const { userId, isGuest, status } = useSelector((state) => state.main);
  
  const [openShareProfileDialog, setOpenShareProfileDialog] = useState(false);
  const [readMoreAbout, setReadMoreAbout] = useState(false);
  

  const {
    _id,
    user,
    avatar,
    realDistance,
    email,
    phone,
    about,
    price,
    qualifications,
    title,
    online,
    faceToFace,
    gender,
    level,
    address,
  } = tutor;

  const { firstName, lastName } = user || {};
  const userAvatar = getAvatarUrl(avatar, gender);


  const handleShareClick = (e) => {
    e.stopPropagation();
    setOpenShareProfileDialog(true);
  };

  const handleReadMore = (e) => {
    e.stopPropagation();
    setReadMoreAbout(!readMoreAbout);
  };

  const handleCloseSharePopup = () => {
    setOpenShareProfileDialog(false);
  };

  // Format qualifications for display
  const getQualificationChips = () => {
    const chips = [];
    if (qualifications) {
      Object.entries(qualifications).forEach(([key, value]) => {
        if (value > 0 && key !== 'Student') {
          chips.push(key);
        }
      });
    }
    return chips.slice(0, 3); // Show max 3 qualifications
  };

  // Format about text - preserve HTML structure for proper rendering
  const formatAboutText = (text) => {
    if (!text) return '';
    
    // Clean up the HTML but preserve structure for lists
    let cleanText = text
      .replace(/<br\s*\/?>/gi, '<br>') // Keep <br> tags
      .replace(/<ul[^>]*>/gi, '<ul>') // Clean ul tags
      .replace(/<li[^>]*>/gi, '<li>') // Clean li tags
      .replace(/<p[^>]*>/gi, '<p>') // Clean p tags
      .replace(/<[^>]*>/g, (match) => {
        // Keep only essential HTML tags
        if (['<ul>', '</ul>', '<li>', '</li>', '<p>', '</p>', '<br>'].includes(match)) {
          return match;
        }
        return '';
      })
      .replace(/\s+/g, ' ') // Clean up extra spaces
      .trim();
    
    return cleanText;
  };

  const shouldShowReadMore = about && about.replace(/<[^>]*>/g, '').length > 200;

  const qualificationChips = getQualificationChips();

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3, // Add margin bottom for list spacing
        }}
      >
        <CardContent sx={{ 
          p: 0,
          '&:last-child': {
            paddingBottom: 0
          }
        }}>
          {/* Header Section */}
          <Box sx={{ p: 2.5, pb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Avatar */}
              <Avatar
                src={userAvatar}
                sx={{
                  width: 100,
                  height: 100,
                  border: `3px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.15)}`,
                  backgroundColor: userAvatar ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: '2rem',
                  fontWeight: 600,
                  '& .MuiAvatar-img': {
                    objectFit: 'cover'
                  }
                }}
              >
                {!userAvatar && `${firstName?.[0] || ''}${lastName?.[0] || ''}`}
              </Avatar>

              {/* Main Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.text.primary, 
                      mb: 0.25,
                      fontSize: '1.1rem'
                    }}>
                      {firstName} {lastName && lastName[0]}.
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.primary.main, 
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        mb: 0.5,
                        fontSize: '0.9rem'
                      }}
                    >
                      {title}
                    </Typography>
                    
                    {/* Contact Info - Compact */}
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                      {email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailOutlined fontSize="small" sx={{ color: theme.palette.text.secondary, fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {userId && !isGuest
                              ? email
                              : userId && isGuest && status === 1
                              ? email
                              : "******@gmail.com"}
                          </Typography>
                        </Box>
                      )}
                      {phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneOutlined fontSize="small" sx={{ color: theme.palette.text.secondary, fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {userId && !isGuest
                              ? phone
                              : userId && isGuest && status === 1
                              ? phone
                              : "*******000"}
                          </Typography>
                        </Box>
                      )}
                      {realDistance !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnOutlined fontSize="small" sx={{ color: theme.palette.success.main, fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.8rem' }}>
                            {realDistance.toFixed(1)} miles
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Share Profile">
                        <IconButton
                          size="small"
                          onClick={handleShareClick}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/tutor/${_id}${locationUrl.search}`)}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 0.25,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          }
                        }}
                      >
                        View Profile →
                      </Button>
                    </Stack>
                    
                    {/* Availability Icons with Tooltips */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {online && (
                        <Tooltip title="Online tutoring available" arrow>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            border: `1px solid ${theme.palette.primary.main}`,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              transform: 'scale(1.1)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <Laptop fontSize="small" sx={{ color: theme.palette.primary.main, fontSize: 12 }} />
                          </Box>
                        </Tooltip>
                      )}
                      {faceToFace && (
                        <Tooltip title="In-person tutoring available" arrow>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            border: `1px solid ${theme.palette.secondary.main}`,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                              transform: 'scale(1.1)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`
                            },
                            transition: 'all 0.3s ease'
                          }}>
                            <Person fontSize="small" sx={{ color: theme.palette.secondary.main, fontSize: 12 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* About Section */}
          <Box sx={{ p: 2.5, pt: 1.5, pb: 1.5 }}>
            <Box sx={{ position: 'relative' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary, 
                  lineHeight: 1.5,
                  fontSize: '0.875rem',
                  // When collapsed, clamp to 3 lines using webkit line-clamp for
                  // consistent truncation across different HTML content.
                  ...(readMoreAbout
                    ? {}
                    : {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                  '& ul': {
                    margin: 0,
                    paddingLeft: 0,
                    listStyleType: 'none',
                    fontSize: '0.875rem'
                  },
                  '& li': {
                    marginBottom: 0.5,
                    position: 'relative',
                    paddingLeft: '1.2em',
                    fontSize: '0.875rem',
                    '&::before': {
                      content: '"•"',
                      position: 'absolute',
                      left: 0,
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem'
                    }
                  },
                  '& p': {
                    // Remove margins so paragraph spacing doesn't consume the
                    // clamped height and prematurely cut the content.
                    margin: 0,
                    fontSize: '0.875rem'
                  }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: formatAboutText(about)
                }}
              />
              {shouldShowReadMore && (
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mt: 0.5
                }}>
                  <Typography
                    component="span"
                    onClick={handleReadMore}
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}
                  >
                    {readMoreAbout ? 'Read less' : 'Read more'} →
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Bottom Section */}
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              {/* Left: Tutoring Info */}
              <Box sx={{ flex: 1 }}>
                {/* Tutoring Levels */}
                <Typography variant="body2" sx={{ 
                  fontWeight: 600, 
                  mb: 1, 
                  color: theme.palette.text.primary,
                  fontSize: '0.8rem'
                }}>
                  Tutoring: {level && Object.entries(level)
                    .filter(([key, value]) => value)
                    .map(([key, value], index, array) => (
                      <span key={index}>
                        {key}
                        {index < array.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                </Typography>
                
                {/* Qualification Chips */}
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {qualificationChips.map((qual, index) => (
                    <Chip
                      key={index}
                      label={qual}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        fontWeight: 500,
                        '& .MuiChip-label': {
                          px: 0.75
                        }
                      }}
                    />
                  ))}
                </Stack>

              </Box>

              {/* Right: Price Circle */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minWidth: 'fit-content'
              }}>
                <Box sx={{ 
                  position: 'relative', 
                  width: 72, 
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={circleContainer} 
                    alt="Price Circle" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    lineHeight: 1,
                    maxWidth: '90%'
                  }}>
                    <Typography variant="body1" sx={{ 
                      fontWeight: 800, 
                      color: '#712d94',
                      fontSize: price && price >= 100 ? '1rem' : '1.2rem',
                      lineHeight: 1,
                      wordBreak: 'break-all'
                    }}>
                      £{price}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: '#712d94',
                      fontSize: price && price >= 100 ? '0.5rem' : '0.6rem',
                      fontWeight: 600,
                      display: 'block'
                    }}>
                      /hr
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Share Profile Dialog */}
      <ShareProfileDialog
        open={openShareProfileDialog}
        handleClose={handleCloseSharePopup}
        tutorId={_id}
        tutorName={`${firstName} ${lastName && lastName[0]}.`}
      />
    </>
  );
};

export default ModernTutorCard;
